import React, { useState, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronRight, FileJson, Image, RefreshCw, X } from 'lucide-react';
import { Component } from '../../../../core/types/gameobject.types';
import type { BackendGameComponentData } from '../../../../core/types/backend-game.types';
import { useSceneStore } from '../../../store/sceneStore';
import { useScriptEditorStore } from '../../../store/scriptEditorStore';
import { usePanelStore } from '../../../store/panelStore';
import { useProjectPanelStore } from '../../../store/projectPanelStore';
import { useBackendGameStore } from '../../../store/backendGameStore';
import { augmentSymbolsWithGolden, remapNormalSymbolCodes } from '../../../services/backend-game/GoldenSymbolGenerator';
import { useAssetUrl } from '../../../hooks/useAssetUrl';
import { cn } from '../../../utils/cn';

interface BackendGameInspectorProps {
  objectId: string;
  component: Component;
}

type SectionId = 'info' | 'mode' | 'grid' | 'symbols' | 'textures' | 'stats' | 'play';

const Section: React.FC<{
  title: string;
  id: SectionId;
  expanded: Set<SectionId>;
  toggle: (id: SectionId) => void;
  children: React.ReactNode;
}> = ({ title, id, expanded, toggle, children }) => {
  const isOpen = expanded.has(id);
  return (
    <div className="border-b border-zinc-700 last:border-b-0">
      <button
        className="flex items-center gap-1.5 w-full px-2 py-1.5 text-xs font-semibold text-zinc-300 hover:bg-zinc-700/50"
        onClick={() => toggle(id)}
      >
        {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {title}
      </button>
      {isOpen && <div className="px-2 pb-2 space-y-2">{children}</div>}
    </div>
  );
};

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-xs text-zinc-500 whitespace-nowrap">{label}</span>
    <span className="text-xs text-zinc-300 truncate text-right max-w-[160px]" title={value}>
      {value || '-'}
    </span>
  </div>
);

const CATEGORY_BADGE_COLORS: Record<string, string> = {
  high: 'bg-red-900/50 text-red-300',
  normal: 'bg-blue-900/50 text-blue-300',
  scatter: 'bg-yellow-900/50 text-yellow-300',
  bonus: 'bg-pink-900/50 text-pink-300',
  wild: 'bg-orange-900/50 text-orange-300',
  golden: 'bg-yellow-900/50 text-yellow-200',
  system: 'bg-zinc-700 text-zinc-400',
};

const SymbolTexturePicker: React.FC<{
  symbolCode: string;
  texturePath?: string;
  onAssign: (path: string) => void;
  onClear: () => void;
}> = ({ symbolCode, texturePath, onAssign, onClear }) => {
  const [dragOver, setDragOver] = useState(false);
  const { url, loading } = useAssetUrl(texturePath ?? null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    // Project panel sends asset ID as text/plain
    const assetId = e.dataTransfer.getData('text/plain');
    if (!assetId) return;

    const assets = useProjectPanelStore.getState().assets;
    const asset = assets[assetId];
    if (!asset || asset.type !== 'sprite') return;

    onAssign(asset.path);
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-1.5 py-1 rounded border',
        dragOver ? 'border-blue-500 bg-blue-500/10' : 'border-zinc-700 bg-zinc-800',
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <span className="text-xs text-zinc-400 font-mono w-8 shrink-0">{symbolCode}</span>
      <div className="flex-1 flex items-center gap-1 min-w-0">
        {loading && <div className="w-6 h-6 bg-zinc-700 animate-pulse rounded shrink-0" />}
        {!loading && url && (
          <img src={url} alt={symbolCode} className="w-6 h-6 object-cover rounded shrink-0" />
        )}
        {!loading && !url && (
          <div className="w-6 h-6 flex items-center justify-center bg-zinc-700 rounded shrink-0">
            <Image size={12} className="text-zinc-500" />
          </div>
        )}
        <span className="text-xs text-zinc-500 truncate">
          {texturePath ? texturePath.split('/').pop() : 'Drop image'}
        </span>
      </div>
      {texturePath && (
        <button
          onClick={onClear}
          className="shrink-0 p-1 rounded hover:bg-red-500/20 text-zinc-500 hover:text-red-400 transition-colors"
          title="Remove texture"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
};

export const BackendGameInspector: React.FC<BackendGameInspectorProps> = ({
  objectId,
  component,
}) => {
  const updateComponent = useSceneStore((s) => s.updateComponent);
  const { showPanel } = usePanelStore();

  const [expanded, setExpanded] = useState<Set<SectionId>>(
    new Set(['info', 'mode', 'symbols', 'play'])
  );

  const toggle = useCallback((id: SectionId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const update = useCallback(
    (key: string, value: unknown) => {
      updateComponent(objectId, component.id, { [key]: value });
    },
    [objectId, component.id, updateComponent]
  );

  const data = component.data as unknown as BackendGameComponentData;
  const cfg = data.backendConfig;

  const modeOptions = useMemo(() => {
    if (!cfg?.modes) return [];
    return cfg.modes.map((m, i) => ({
      value: i,
      label: m.name || `Mode ${i}`,
      description: m.description || '',
      isBuyFeature: m.isBuyFeature,
    }));
  }, [cfg]);

  const nonSystemSymbols = useMemo(() => {
    if (!cfg?.symbols) return [];
    const augmented = augmentSymbolsWithGolden(cfg.symbols, cfg.mechanics);
    const remapped = remapNormalSymbolCodes(augmented);
    return remapped.filter((s) => s.category !== 'system');
  }, [cfg]);

  const handleOpenConfigJson = useCallback(() => {
    if (!cfg) return;
    const content = JSON.stringify(cfg, null, 2);
    const title = `${data.name || data.gameName || 'Game'} Config`;
    const store = useScriptEditorStore.getState() as any;
    if (store.openJsonConfig) {
      store.openJsonConfig(objectId, component.id, title, content);
      showPanel('script-editor');
    }
  }, [cfg, data.name, data.gameName, objectId, component.id, showPanel]);

  const [resetting, setResetting] = useState(false);

  const handleResetConfig = useCallback(async () => {
    if (!data.gameName) return;
    setResetting(true);
    try {
      // Clear cache so we get fresh data from API
      useBackendGameStore.getState().clearCache();
      const configResponse = await useBackendGameStore.getState().loadGameConfig(data.gameName);
      update('backendConfig', configResponse.config);

      // Also refresh the script editor tab if open via openJsonConfig (resets content + isDirty)
      const scriptStore = useScriptEditorStore.getState() as any;
      const tabId = `backend-config-${objectId}`;
      if (scriptStore.files[tabId] && scriptStore.openJsonConfig) {
        const title = `${data.name || data.gameName || 'Game'} Config`;
        scriptStore.openJsonConfig(objectId, component.id, title, JSON.stringify(configResponse.config, null, 2));
      }
    } catch (err) {
      console.error('[BackendGameInspector] Failed to reset config:', err);
    } finally {
      setResetting(false);
    }
  }, [data.gameName, objectId, update]);

  const handleTextureAssign = useCallback(
    (symbolCode: string, path: string) => {
      const textures = { ...(data.symbolTextures || {}), [symbolCode]: path };
      update('symbolTextures', textures);
    },
    [data.symbolTextures, update]
  );

  const handleTextureClear = useCallback(
    (symbolCode: string) => {
      const textures = { ...(data.symbolTextures || {}) };
      delete textures[symbolCode];
      update('symbolTextures', textures);
    },
    [data.symbolTextures, update]
  );

  // Play settings: bet levels from backend or custom override
  const effectiveBetLevels: number[] = data.customBetLevels ?? cfg?.options?.totalBet ?? [];
  const [betLevelInput, setBetLevelInput] = useState('');

  const handleAddBetLevel = useCallback(() => {
    const val = Number(betLevelInput);
    if (!val || val <= 0) return;
    const levels = [...effectiveBetLevels, val].sort((a, b) => a - b);
    // Remove duplicates
    const unique = [...new Set(levels)];
    update('customBetLevels', unique);
    setBetLevelInput('');
  }, [betLevelInput, effectiveBetLevels, update]);

  const handleRemoveBetLevel = useCallback(
    (level: number) => {
      const levels = effectiveBetLevels.filter((l) => l !== level);
      update('customBetLevels', levels.length > 0 ? levels : null);
    },
    [effectiveBetLevels, update]
  );

  const handleResetBetLevels = useCallback(() => {
    update('customBetLevels', null);
  }, [update]);

  if (!cfg) {
    return (
      <div className="px-2 py-3 text-xs text-zinc-500 text-center">
        No backend config loaded.
        <br />
        Create this object from the
        <br />
        GameObject &gt; Games menu.
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* Game Info */}
      <Section title="Game Info" id="info" expanded={expanded} toggle={toggle}>
        <InfoRow label="Name" value={data.name} />
        <InfoRow label="Game ID" value={data.gameId} />
        <InfoRow label="Version" value={data.version} />
        <InfoRow label="Description" value={data.description} />
        <InfoRow label="Game Key" value={data.gameName} />
        <button
          onClick={handleOpenConfigJson}
          className={cn(
            'flex items-center gap-1.5 w-full mt-1 px-2 py-1.5 text-xs rounded',
            'bg-zinc-700 hover:bg-zinc-600 text-zinc-300 transition-colors'
          )}
        >
          <FileJson size={12} />
          Open Config JSON
        </button>
        <button
          onClick={handleResetConfig}
          disabled={resetting}
          className={cn(
            'flex items-center gap-1.5 w-full mt-1 px-2 py-1.5 text-xs rounded',
            resetting
              ? 'bg-zinc-700 text-zinc-500 cursor-wait'
              : 'bg-amber-700/50 hover:bg-amber-600/50 text-amber-300 transition-colors'
          )}
        >
          <RefreshCw size={12} className={resetting ? 'animate-spin' : ''} />
          {resetting ? 'Fetching from API...' : 'Reset Config from API'}
        </button>
      </Section>

      {/* Play Settings */}
      <Section title="Play Settings" id="play" expanded={expanded} toggle={toggle}>
        {/* Starting Balance */}
        <div className="space-y-1">
          <label className="text-xs text-zinc-500">Initial Balance</label>
          <input
            type="number"
            value={data.startingBalance ?? 10000}
            onChange={(e) => update('startingBalance', Number(e.target.value))}
            min={0}
            step={1000}
            className={cn(
              'w-full px-2 py-1.5 text-xs bg-zinc-800 border border-zinc-700 rounded',
              'text-zinc-300 focus:outline-none focus:border-blue-500'
            )}
          />
        </div>

        {/* Bet Levels */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-xs text-zinc-500">Bet Levels</label>
            {data.customBetLevels && (
              <button
                onClick={handleResetBetLevels}
                className="text-[10px] text-blue-400 hover:text-blue-300"
              >
                Reset to default
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1">
            {effectiveBetLevels.map((level) => (
              <div
                key={level}
                className="flex items-center gap-0.5 px-1.5 py-0.5 bg-zinc-700 rounded text-xs text-zinc-300"
              >
                <span>{level.toLocaleString()}</span>
                <button
                  onClick={() => handleRemoveBetLevel(level)}
                  className="text-zinc-500 hover:text-red-400 ml-0.5"
                  title="Remove"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={betLevelInput}
              onChange={(e) => setBetLevelInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddBetLevel()}
              placeholder="Add bet level..."
              min={1}
              className={cn(
                'flex-1 px-2 py-1 text-xs bg-zinc-800 border border-zinc-700 rounded',
                'text-zinc-300 focus:outline-none focus:border-blue-500',
                'placeholder:text-zinc-600'
              )}
            />
            <button
              onClick={handleAddBetLevel}
              className="px-2 py-1 text-xs bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded"
            >
              Add
            </button>
          </div>
          {!data.customBetLevels && (
            <p className="text-[10px] text-zinc-600">
              Using {cfg.options.totalBet.length} levels from backend config
            </p>
          )}
        </div>
      </Section>

      {/* Mode Selector */}
      <Section title="Mode" id="mode" expanded={expanded} toggle={toggle}>
        <div className="space-y-1">
          <label className="text-xs text-zinc-500">Active Mode</label>
          <div className="relative">
            <select
              value={data.activeMode ?? 0}
              onChange={(e) => update('activeMode', Number(e.target.value))}
              className={cn(
                'w-full px-2 py-1.5 pr-8 text-xs bg-zinc-800 border border-zinc-700 rounded',
                'text-zinc-300 focus:outline-none focus:border-blue-500',
                'appearance-none cursor-pointer'
              )}
            >
              {modeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}{opt.isBuyFeature ? ' (Buy Feature)' : ''}
                </option>
              ))}
            </select>
            <ChevronDown
              size={12}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none"
            />
          </div>
          {modeOptions[data.activeMode ?? 0]?.description && (
            <p className="text-xs text-zinc-500 mt-0.5">
              {modeOptions[data.activeMode ?? 0].description}
            </p>
          )}
        </div>
      </Section>

      {/* Grid Info */}
      <Section title="Grid" id="grid" expanded={expanded} toggle={toggle}>
        <InfoRow label="Rows" value={String(cfg.grid.rows)} />
        <InfoRow label="Columns" value={String(cfg.grid.cols)} />
        <InfoRow
          label="Total Cells"
          value={String(cfg.grid.rows * cfg.grid.cols)}
        />
      </Section>

      {/* Symbols */}
      <Section title="Symbols" id="symbols" expanded={expanded} toggle={toggle}>
        <div className="space-y-1">
          {nonSystemSymbols.map((sym) => (
            <div
              key={sym.code}
              className="flex items-center gap-2 px-1.5 py-1 rounded bg-zinc-800/50"
            >
              <span className="text-xs font-mono text-zinc-400 w-6 text-center">
                {sym.code}
              </span>
              <span className="text-xs text-zinc-300 flex-1 truncate">
                {sym.name}
              </span>
              <span
                className={cn(
                  'text-[10px] px-1.5 py-0.5 rounded-full',
                  CATEGORY_BADGE_COLORS[sym.category] || 'bg-zinc-700 text-zinc-400'
                )}
              >
                {sym.category}
              </span>
            </div>
          ))}
        </div>
      </Section>

      {/* Texture Assignment */}
      <Section title="Symbol Textures" id="textures" expanded={expanded} toggle={toggle}>
        <p className="text-xs text-zinc-500 mb-1">
          Drag images from the Project panel to assign textures.
        </p>
        <div className="space-y-1">
          {nonSystemSymbols.map((sym) => (
            <SymbolTexturePicker
              key={sym.code}
              symbolCode={sym.code}
              texturePath={data.symbolTextures?.[sym.code]}
              onAssign={(path) => handleTextureAssign(sym.code, path)}
              onClear={() => handleTextureClear(sym.code)}
            />
          ))}
        </div>
      </Section>

      {/* Key Stats */}
      <Section title="Key Stats" id="stats" expanded={expanded} toggle={toggle}>
        <InfoRow label="Max Win" value={`${cfg.rules.maxWin}x`} />
        <InfoRow label="Max Win Formula" value={cfg.rules.maxWinFormula} />
        <InfoRow label="Trigger Count" value={String(cfg.rules.triggerCount)} />
        <InfoRow label="Free Spins" value={String(cfg.rules.freeSpins)} />
        <InfoRow label="Retrigger Count" value={String(cfg.rules.retriggerCount)} />
        <InfoRow label="Add Free Spins" value={String(cfg.rules.addFreeSpins)} />
        <InfoRow label="Min Matches" value={String(cfg.rules.minNumOfKind)} />
        <InfoRow label="Max Matches" value={String(cfg.rules.maxNumOfKind)} />
        <InfoRow label="Tumble" value={cfg.mechanics.tumble ? 'Yes' : 'No'} />
        <InfoRow label="Buy Feature" value={cfg.mechanics.buyFeature ? 'Yes' : 'No'} />
        <InfoRow label="Super Scatter" value={cfg.mechanics.superScatter ? 'Yes' : 'No'} />
        <InfoRow
          label="Bet Levels"
          value={cfg.options.totalBet.length > 0 ? `${cfg.options.totalBet.length} levels` : '-'}
        />
      </Section>
    </div>
  );
};
